import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Award, PenTool, TrendingUp, FileText, Loader2, LogOut, Repeat } from 'lucide-react';
import { useContext } from 'react';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WritingResults = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { setUser } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Çıkış yapılırken hata oluştu');
    }
  };
  
  useEffect(() => {
    loadSession();
  }, [sessionId]);
  
  const loadSession = async () => {
    try {
      const response = await axios.get(`${API}/writing/history`, {
        withCredentials: true
      });
      const foundSession = response.data.find(s => s.id === sessionId);
      if (foundSession) {
        setSession(foundSession);
      } else {
        toast.error('Sonuçlar bulunamadı');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Sonuçlar yüklenemedi');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-600">Sonuçlar yükleniyor...</p>
        </div>
      </div>
    );
  }
  
  if (!session || !session.submissions) return null;
  
  const averageScore = session.overall_score || 0;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Top Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/writing-test')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-xl shadow-lg w-full sm:w-auto"
          >
            <Repeat className="w-5 h-5 mr-2" />
            Yeni Test Başlat 🎯
          </Button>
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border-gray-300 hover:bg-gray-50 font-semibold px-6 py-3 rounded-xl w-full sm:w-auto"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Geri
          </Button>
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-8">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                <PenTool className="w-12 h-12 text-white" />
              </div>
            </div>
            <CardTitle className="text-3xl gradient-text">Writing Test Sonuçları</CardTitle>
            <p className="text-gray-600 mt-2">Seviye: {session.level}</p>
          </CardHeader>
          <CardContent>
            <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg mb-8">
              <TrendingUp className="w-12 h-12 text-purple-600 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-2">Genel Puan</p>
              <p className="text-5xl font-bold text-purple-600 mb-2">{averageScore.toFixed(1)}/100</p>
              <Progress value={averageScore} className="h-3" />
            </div>
            
            <div className="space-y-8">
              {session.submissions.map((submission, index) => {
                const evaluation = submission.evaluation;
                return (
                  <Card key={index} className="border border-purple-200">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-600" />
                        Görev {index + 1}
                      </CardTitle>
                      <p className="text-sm text-gray-600 mt-2">{submission.prompt}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Yazınız:</p>
                        <p className="text-gray-800 whitespace-pre-line">{submission.text}</p>
                        <p className="text-xs text-gray-500 mt-2">Kelime sayısı: {evaluation.word_count}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="text-center p-3 bg-blue-50 rounded">
                          <p className="text-xs text-gray-600 mb-1">Genel</p>
                          <p className="text-2xl font-bold text-blue-600">{evaluation.overall_score}</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded">
                          <p className="text-xs text-gray-600 mb-1">Dilbilgisi</p>
                          <p className="text-2xl font-bold text-green-600">{evaluation.grammar_score}</p>
                        </div>
                        <div className="text-center p-3 bg-purple-50 rounded">
                          <p className="text-xs text-gray-600 mb-1">Kelime</p>
                          <p className="text-2xl font-bold text-purple-600">{evaluation.vocabulary_score}</p>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded">
                          <p className="text-xs text-gray-600 mb-1">Görev</p>
                          <p className="text-2xl font-bold text-orange-600">{evaluation.task_achievement_score}</p>
                        </div>
                        <div className="text-center p-3 bg-pink-50 rounded">
                          <p className="text-xs text-gray-600 mb-1">Tutarlılık</p>
                          <p className="text-2xl font-bold text-pink-600">{evaluation.coherence_score}</p>
                        </div>
                      </div>
                      
                      {/* Türkçe Feedback */}
                      <div className="bg-blue-50 p-4 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-2">🇹🇷 Değerlendirme (Türkçe):</p>
                        <p className="text-sm text-gray-800">{evaluation.feedback_tr}</p>
                      </div>
                      
                      {/* English Feedback */}
                      <div className="bg-green-50 p-4 rounded">
                        <p className="text-sm font-semibold text-gray-700 mb-2">🇬🇧 Evaluation (English):</p>
                        <p className="text-sm text-gray-800">{evaluation.feedback_en}</p>
                      </div>
                      
                      {/* Strengths */}
                      {evaluation.strengths && evaluation.strengths.length > 0 && (
                        <div className="bg-green-50 p-4 rounded">
                          <p className="text-sm font-semibold text-gray-700 mb-2">✅ Güçlü Yönler:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {evaluation.strengths.map((strength, i) => (
                              <li key={i} className="text-sm text-gray-800">{strength}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Areas for Improvement */}
                      {evaluation.areas_for_improvement && evaluation.areas_for_improvement.length > 0 && (
                        <div className="bg-orange-50 p-4 rounded">
                          <p className="text-sm font-semibold text-gray-700 mb-2">💡 Geliştirilmesi Gereken Alanlar:</p>
                          <ul className="list-disc list-inside space-y-1">
                            {evaluation.areas_for_improvement.map((area, i) => (
                              <li key={i} className="text-sm text-gray-800">{area}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-center gap-4">
          <Button
            onClick={() => navigate('/exercises')}
            size="lg"
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
          >
            Diğer Egzersizler 🎯
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WritingResults;